import type { LifetimeConfigParams, LifetimeParameters } from '@hub/bearing-hub/common/types';
import { lifetimeConfigParams } from '@hub/bearing-hub/common/constants';
import { isObject } from '@hub/libs/utils';
import type {
  IbhCatalogArticleParams,
  IbhLifetimePartLinearAndPivotingRequestDto,
  IbhLifetimePartLinearAndRotationRequestDto,
  IbhLifetimePartLinearRequestDto,
  IbhLifetimePartPivotingRequestDto,
  IbhLifetimePartRotationRequestDto,
} from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import {
  convertLengthToMillimeters,
  createLifetimeConfigPayload,
} from '@hub/bearing-hub/libs/BearingsLifetime/BearingLifetimeFactory';
import { createContext, type ReactNode, useContext, useRef } from 'react';
import {
  DefaultServiceLifeForBearings,
  DefaultServiceLifeForBearingsForUS,
} from '@hub/bearing-hub/libs/BearingsLifetime/constants';
import type { StoreApi } from 'zustand';
import { createStore, useStore } from 'zustand';
import { UnitSystem } from '@igus/kopla-domain';
import { ForceCode, LengthCode } from '@icommerce/units';
import { BearingHubHelper } from '@hub/libs/bearing-hub.helper';

export type LifetimeConfig =
  | IbhLifetimePartLinearRequestDto
  | IbhLifetimePartPivotingRequestDto
  | IbhLifetimePartRotationRequestDto
  | IbhLifetimePartLinearAndPivotingRequestDto
  | IbhLifetimePartLinearAndRotationRequestDto;

type FlatLifetimeConfig = Pick<
  IbhCatalogArticleParams,
  Extract<keyof LifetimeParameters, keyof IbhCatalogArticleParams>
>;

type LifetimeConfigPayload<FlatStructure extends boolean> = FlatStructure extends true
  ? FlatLifetimeConfig
  : LifetimeConfig;

export interface IbhLifetimeValuesStore {
  lifetimeParameters: LifetimeParameters;
  formUnitSystem: UnitSystem;
  setFormUnitSystem: (unitSystem: UnitSystem) => LifetimeParameters;
  setLifetimeParameter: (lifetimeParameter: LifetimeParameters) => void;
  setLifetimeParametersValue: (key: LifetimeConfigParams, value: number | string | undefined) => void;
  hasLifetimeParameterValues: () => boolean;
  getTransformedLifetimeParameters: () => LifetimeParameters;
  getDefaultLifetimeParameters: () => LifetimeParameters;
  resetLifetimeParametersToDefault: (query?: Partial<LifetimeParameters>) => void;
  getLifetimeConfigPayload: <FlatStructure extends boolean = false>(
    flatStructure?: FlatStructure
  ) => LifetimeConfigPayload<FlatStructure>;
}

const getDefaultLifetimeParametersForUnitSystem = (unitSystem: UnitSystem): LifetimeParameters =>
  unitSystem === UnitSystem.metric
    ? { ...DefaultServiceLifeForBearings, force: DefaultServiceLifeForBearings.load.force }
    : { ...DefaultServiceLifeForBearingsForUS, force: DefaultServiceLifeForBearingsForUS.load.force };

export const convertLifetimeParametersUnitSystem = (
  lifetimeParameters: LifetimeParameters,
  fromUnitSystem: UnitSystem,
  toUnitSystem: UnitSystem
): LifetimeParameters => {
  const fromForceUnit = fromUnitSystem === UnitSystem.metric ? ForceCode.NEWTON : ForceCode.POUND_FORCE;
  const toForceUnit = toUnitSystem === UnitSystem.metric ? ForceCode.NEWTON : ForceCode.POUND_FORCE;
  const fromLengthUnit = fromUnitSystem === UnitSystem.metric ? LengthCode.MILLIMETER : LengthCode.INCH;
  const convertLength = (value?: number) => {
    if (toUnitSystem === UnitSystem.metric) {
      return convertLengthToMillimeters(value, fromUnitSystem);
    }

    return value == null ? value : BearingHubHelper.convertLengthUnit({ value, unit: fromLengthUnit }, LengthCode.INCH);
  };

  return {
    ...lifetimeParameters,
    force:
      lifetimeParameters.force == null
        ? lifetimeParameters.force
        : BearingHubHelper.convertForceUnit({ value: lifetimeParameters.force, unit: fromForceUnit }, toForceUnit),
    acceptableWear: convertLength(lifetimeParameters.acceptableWear),
    linearStrokeLength: convertLength(lifetimeParameters.linearStrokeLength),
  };
};

const transformValueToProperType = (lifetimeParameters: LifetimeParameters) => {
  const expectedString = [
    lifetimeConfigParams.environment,
    lifetimeConfigParams.oppositeMaterialId,
    lifetimeConfigParams.temporalTypeOfMovement,
    lifetimeConfigParams.intermittentParams,
    lifetimeConfigParams.typeOfMovement,
    lifetimeConfigParams.typeOfOperation,
  ] as LifetimeConfigParams[];

  let transformedParameters = lifetimeParameters;

  Object.keys(lifetimeParameters).forEach((key: keyof LifetimeParameters) => {
    if (isObject(lifetimeParameters[key])) {
      return;
    }

    transformedParameters = {
      ...transformedParameters,
      [key]: expectedString.includes(key)
        ? lifetimeParameters[key].toString()
        : parseFloat(lifetimeParameters[key] as string),
    };
  });

  return transformedParameters;
};

export const createLifetimeParametersStore = (initUnitSystem: UnitSystem) =>
  createStore<IbhLifetimeValuesStore>((set, get) => ({
    lifetimeParameters: getDefaultLifetimeParametersForUnitSystem(initUnitSystem),
    formUnitSystem: initUnitSystem,

    setFormUnitSystem: (unitSystem: UnitSystem) => {
      const state = get();
      const lifetimeParameters = convertLifetimeParametersUnitSystem(
        state.lifetimeParameters,
        state.formUnitSystem,
        unitSystem
      );

      set({ formUnitSystem: unitSystem, lifetimeParameters });

      return lifetimeParameters;
    },

    setLifetimeParameter: lifetimeParameter =>
      set(state => ({
        lifetimeParameters: { ...state.lifetimeParameters, ...lifetimeParameter },
      })),

    setLifetimeParametersValue: (key, value) =>
      set(state => ({
        lifetimeParameters: { ...state.lifetimeParameters, [key]: value },
      })),

    hasLifetimeParameterValues: () => Object.keys(get().lifetimeParameters).length > 0,

    getTransformedLifetimeParameters: () => transformValueToProperType(get().lifetimeParameters),

    getDefaultLifetimeParameters: () => getDefaultLifetimeParametersForUnitSystem(get().formUnitSystem),

    resetLifetimeParametersToDefault: (query?: Partial<LifetimeParameters>) =>
      set({ lifetimeParameters: { ...get().getDefaultLifetimeParameters(), ...query } }),

    getLifetimeConfigPayload: <FlatStructure extends boolean = false>(flatStructure?: FlatStructure) => {
      const payload = createLifetimeConfigPayload(
        get().getTransformedLifetimeParameters(),
        get().formUnitSystem,
        flatStructure
      );
      const typeOfMovement = Object.keys(payload)[0];

      return payload[typeOfMovement] as LifetimeConfigPayload<FlatStructure>;
    },
  }));

const LifetimeParametersStoreContext = createContext<StoreApi<IbhLifetimeValuesStore> | null>(null);

interface LifetimeParametersProviderProps {
  unitSystem: UnitSystem;
  children: ReactNode;
}

export const LifetimeParametersStoreProvider = ({ unitSystem, children }: LifetimeParametersProviderProps) => {
  const storeRef = useRef<StoreApi<IbhLifetimeValuesStore>>();
  if (!storeRef.current) {
    storeRef.current = createLifetimeParametersStore(unitSystem);
  }

  return (
    <LifetimeParametersStoreContext.Provider value={storeRef.current}>
      {children}
    </LifetimeParametersStoreContext.Provider>
  );
};

export const useLifetimeParametersStore = <T = IbhLifetimeValuesStore,>(
  selector?: (state: IbhLifetimeValuesStore) => T
): T => {
  const store = useContext(LifetimeParametersStoreContext);
  if (!store) {
    throw new Error('useLifetimeParametersStore must be used inside LifetimeParametersStoreProvider');
  }
  return useStore(store, selector ?? (s => s as unknown as T));
};
